/**
 * PriceRangeSlider Component
 *
 * Dual-handle range slider for price filtering.
 * Allows users to set min and max price with visual slider and numeric inputs.
 *
 * SOLID Compliance:
 * - SRP: Only handles price range selection UI
 * - OCP: Extensible via props (min, max, step, currency)
 * - DIP: Depends on abstractions (onChange callback, not URLStateManager directly)
 *
 * Usage:
 * <PriceRangeSlider
 *   minPrice={0}
 *   maxPrice={500}
 *   value={{ min: 100, max: 300 }}
 *   onChange={(min, max) => updateFilters({ minPrice: min, maxPrice: max })}
 *   currency="$"
 * />
 *
 * Location: apps/web/src/components/search/PriceRangeSlider.jsx
 * Created: 2025-10-31
 * Part of: MVP 1.9 - Adaptive Search & Filter System - Phase 2
 */

import React, { useState, useEffect, useRef } from 'react';

/**
 * PriceRangeSlider Component
 * @param {number} minPrice - Minimum allowed price (default: 0)
 * @param {number} maxPrice - Maximum allowed price (default: 1000)
 * @param {Object} value - Current selected range: { min, max }
 * @param {Function} onChange - Callback when range changes: onChange(min, max)
 * @param {string} currency - Currency symbol (default: '$')
 * @param {number} step - Step increment (default: 10)
 */
export default function PriceRangeSlider({
  minPrice = 0,
  maxPrice = 1000,
  value = { min: minPrice, max: maxPrice },
  onChange = () => {},
  currency = '$',
  step = 10
}) {
  // Local state for slider values (synced with prop value)
  const [localMin, setLocalMin] = useState(value.min || minPrice);
  const [localMax, setLocalMax] = useState(value.max || maxPrice);
  const [isDragging, setIsDragging] = useState(false);

  // Refs for input fields
  const minInputRef = useRef(null);
  const maxInputRef = useRef(null);

  // Sync local state with prop value
  useEffect(() => {
    setLocalMin(value.min || minPrice);
    setLocalMax(value.max || maxPrice);
  }, [value.min, value.max, minPrice, maxPrice]);

  /**
   * Handle min slider change
   */
  const handleMinChange = (  _e) => {
    const newMin = Math.min(Number(_e.target.value), localMax - step);
    setLocalMin(newMin);
    if (!isDragging) {
      onChange(newMin, localMax);
    }
  };

  /**
   * Handle max slider change
   */
  const handleMaxChange = (  _e) => {
    const newMax = Math.max(Number(_e.target.value), localMin + step);
    setLocalMax(newMax);
    if (!isDragging) {
      onChange(localMin, newMax);
    }
  };

  /**
   * Handle min input change
   */
  const handleMinInputChange = (  _e) => {
    const val = _e.target.value.replace(/[^0-9]/g, '');
    if (val === '') {
      setLocalMin(minPrice);
      return;
    }
    const newMin = Math.min(Math.max(Number(val), minPrice), localMax - step);
    setLocalMin(newMin);
    onChange(newMin, localMax);
  };

  /**
   * Handle max input change
   */
  const handleMaxInputChange = (  _e) => {
    const val = _e.target.value.replace(/[^0-9]/g, '');
    if (val === '') {
      setLocalMax(maxPrice);
      return;
    }
    const newMax = Math.max(Math.min(Number(val), maxPrice), localMin + step);
    setLocalMax(newMax);
    onChange(localMin, newMax);
  };

  /**
   * Handle slider mouse up (commit changes)
   */
  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      onChange(localMin, localMax);
    }
  };

  /**
   * Calculate percentage for slider track fill
   */
  const getMinPercent = () => {
    return ((localMin - minPrice) / (maxPrice - minPrice)) * 100;
  };

  const getMaxPercent = () => {
    return ((localMax - minPrice) / (maxPrice - minPrice)) * 100;
  };

  /**
   * Format price for display
   */
  const formatPrice = (price) => {
    return `${currency}${price.toLocaleString()}`;
  };

  return (
    <div className="w-full">
      {/* Price Range Display */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-700">
          <span className="font-semibold">Price Range</span>
        </div>
        <div className="text-sm font-medium text-gray-900">
          {formatPrice(localMin)} - {formatPrice(localMax)}
        </div>
      </div>

      {/* Dual Range Slider */}
      <div className="relative mb-12">
        {/* Track background */}
        <div className="absolute w-full h-1.5 bg-gray-200 rounded-full top-1/2 transform -translate-y-1/2"></div>

        {/* Track active range (filled) */}
        <div
          className="absolute h-1.5 bg-blue-600 rounded-full top-1/2 transform -translate-y-1/2 pointer-events-none"
          style={{
            left: `${getMinPercent()}%`,
            right: `${100 - getMaxPercent()}%`
          }}
        ></div>

        {/* Min slider */}
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          step={step}
          value={localMin}
          onChange={handleMinChange}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={handleMouseUp}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={handleMouseUp}
          className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-blue-700 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:hover:bg-blue-700 z-10"
        />

        {/* Max slider */}
        <input
          type="range"
          min={minPrice}
          max={maxPrice}
          step={step}
          value={localMax}
          onChange={handleMaxChange}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={handleMouseUp}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={handleMouseUp}
          className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-600 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:hover:bg-blue-700 [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-600 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:hover:bg-blue-700 z-20"
        />
      </div>

      {/* Price Inputs */}
      <div className="grid grid-cols-2 gap-4">
        {/* Min Price Input */}
        <div>
          <label htmlFor="min-price" className="block text-xs font-semibold text-gray-900 mb-1">
            MIN PRICE
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              {currency}
            </span>
            <input
              ref={minInputRef}
              type="text"
              id="min-price"
              name="minPrice"
              value={localMin}
              onChange={handleMinInputChange}
              aria-label="Minimum price"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Max Price Input */}
        <div>
          <label htmlFor="max-price" className="block text-xs font-semibold text-gray-900 mb-1">
            MAX PRICE
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              {currency}
            </span>
            <input
              ref={maxInputRef}
              type="text"
              id="max-price"
              name="maxPrice"
              value={localMax}
              onChange={handleMaxInputChange}
              aria-label="Maximum price"
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Clear button */}
      <button
        onClick={() => {
          setLocalMin(minPrice);
          setLocalMax(maxPrice);
          onChange(minPrice, maxPrice);
        }}
        className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
      >
        Clear price filter
      </button>
    </div>
  );
}
